import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { createResourceLocatorString } from '../../../util/routes';
import css from './SearchPagePills.module.css';
import { BOOLEAN_STRINGS, PARAM_KEYS, UserType } from '../../../util/enums';
import { Icons, PrimaryButtonInline } from '../../../components';
import { capitalizeFirstLetter, isArrayLength } from '../../../util/genericHelpers';

const SearchPagePills = ({ history, routeConfiguration, location, intl }) => {
  // Get current filters from URL
  const getCurrentFilters = () => {
    const queryParams = new URLSearchParams(location.search);
    const filters = {};
    for (const [key, param] of Object.entries(PARAM_KEYS)) {
      if (key !== 'ListingCategory') {
        const values = queryParams.get(param);
        filters[param] = values ? values.split(',') : [];
      }
    }
    return filters;
  };
  // State to manage filters locally
  const [filters, setFilters] = useState(getCurrentFilters);

  // Update local state when URL changes (from other components)
  useEffect(() => {
    setFilters(getCurrentFilters());
  }, [location.search]);

  // Get current listing category
  const getCurrentListingCategory = () => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get(PARAM_KEYS.ListingCategory) || UserType.TALENT;
  };

  const currentListingCategory = getCurrentListingCategory();

  const getCategoryPrefix = category => {
    const displayCategory = category.toLowerCase() === UserType.SCHOOL ? 'Group' : category;
    return `${capitalizeFirstLetter(displayCategory)} - `;
  };
  // Generate pills from local state
  const pills = Object.entries(filters).reduce((acc, [paramKey, values]) => {
    const prefix = getCategoryPrefix(currentListingCategory);
    return [
      ...acc,
      ...values.map(value => {
        let displayLabel;
        if (value === BOOLEAN_STRINGS.TRUE) {
          // For boolean true values, show the key name instead of "true"
          const keyName = paramKey.replace(/^pub_is|^pub_/, ''); // Remove pub_is or pub_ prefix
          displayLabel = `${prefix}${keyName.charAt(0).toUpperCase() + keyName.slice(1)}`;
        } else {
          displayLabel = `${prefix}${value.charAt(0).toUpperCase() +
            value.slice(1).replace(/-/g, ' ')}`;
        }

        return {
          key: paramKey,
          value,
          label: displayLabel,
        };
      }),
    ];
  }, []);

  // Update URL with current filters
  const updateURL = newFilters => {
    const queryParams = new URLSearchParams();
    queryParams.set(PARAM_KEYS.ListingCategory, currentListingCategory);

    Object.entries(newFilters).forEach(([paramKey, values]) => {
      if (values.length > 0) {
        queryParams.set(paramKey, values.join(','));
      }
      // If values array is empty, the parameter won't be added to URL
    });

    const newSearch = queryParams.toString();
    const newPath = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      newSearch
        ? {
            [newSearch.includes('=') ? newSearch.split('=')[0] : newSearch]: newSearch.includes('=')
              ? newSearch.split('=')[1]
              : '',
          }
        : {}
    );

    // Simplified URL update without 'search=' prefix
    const finalPath = `${newPath.split('?')[0]}${newSearch ? '?' + newSearch : ''}`;
    history.push(finalPath);
  };

  // Handle pill removal
  const handleRemovePill = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: filters[key].filter(v => v !== value),
    };

    setFilters(newFilters);
    updateURL(newFilters);
  };

  // Clear all filters but preserve pub_listingCategory
  const handleClearAll = () => {
    const newFilters = {};
    for (const key in filters) {
      newFilters[key] = [];
    }

    setFilters(newFilters);
    updateURL(newFilters);
  };

  return (
    <>
      {isArrayLength(pills) ? (
        <div className={css.pillsContainer}>
          {pills.map(pill => (
            <div key={`${pill.key}-${pill.value}`} className={css.pill}>
              <span className={css.pillLabel}>{pill.label}</span>
              <button
                type="button"
                className={css.removeButton}
                onClick={() => handleRemovePill(pill.key, pill.value)}
                aria-label={intl.formatMessage(
                  { id: 'SearchPagePills.RemoveFilter' },
                  { filter: pill.label }
                )}
              >
                <Icons name="cross" />
              </button>
            </div>
          ))}
          <PrimaryButtonInline
            type="button"
            className={css.clearAllButton}
            onClick={handleClearAll}
          >
            <FormattedMessage id="SearchPagePills.ClearAll" />
          </PrimaryButtonInline>
        </div>
      ) : null}
    </>
  );
};

export default SearchPagePills;
